import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EXPORT_MAX_ROWS,
  assignmentStatusSchema,
  catalogResponseSchema,
  exportDatasetSchema,
  exportResponseSchema,
  inventoryStatusSchema,
  transactionTypeSchema,
  unitsResponseSchema,
  userStatusSchema,
  type ExportDataset,
  type ExportResponse,
} from '@unit/shared';
import { STOREROOM_ROLES } from '../../components/RequireRole.js';
import { ApiError } from '../../components/ApiError.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { useApi } from '../../lib/useApi.js';
import { apiGet, ApiClientError } from '../../lib/api.js';
import { exportFileName, saveBlob, toCsv, toXlsx, type ExportFormat } from './exportFile.js';

const input = 'mt-1 block rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm';

/** The `status` filter means something different per dataset; this is what the picker offers. */
const STATUS_OPTIONS: Record<ExportDataset, { values: readonly string[]; labelPrefix: string }> = {
  soldiers: { values: userStatusSchema.options, labelPrefix: 'status' },
  assignments: { values: assignmentStatusSchema.options, labelPrefix: 'assignmentStatus' },
  inventory: { values: inventoryStatusSchema.options, labelPrefix: 'inventory.status' },
  ledger: { values: transactionTypeSchema.options, labelPrefix: 'ledger.types' },
};

type Result = { kind: 'idle' } | { kind: 'busy' } | { kind: 'done'; table: ExportResponse; filename: string } | { kind: 'error'; code: ApiClientError['code']; requestId: string };

/**
 * CSV/XLSX exports (REQUIREMENTS §2.9). The server produces the rows under
 * the same scope as the screens they mirror; this page only picks the
 * filters and encodes the file. Inventory and the ledger are storeroom-only,
 * so a commander does not see those two datasets at all.
 */
export function ExportsPage() {
  const { t, i18n } = useTranslation();
  const { roles } = useClaimRoles();
  const storeroom = roles.some((r) => STOREROOM_ROLES.includes(r));
  const datasets = exportDatasetSchema.options.filter((d) => storeroom || (d !== 'inventory' && d !== 'ledger'));

  const [dataset, setDataset] = useState<ExportDataset>('soldiers');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [unitId, setUnitId] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [result, setResult] = useState<Result>({ kind: 'idle' });

  const units = useApi('/units', unitsResponseSchema);
  const catalog = useApi('/catalog', catalogResponseSchema);
  const categories = useMemo(
    () => (catalog.kind === 'ready' ? [...new Set(catalog.data.items.map((i) => i.category))].sort() : []),
    [catalog],
  );
  const viewableUnits = useMemo(() => {
    if (units.kind !== 'ready') return [];
    const viewable = new Set(units.data.viewableUnitIds);
    return units.data.units.filter((u) => viewable.has(u.id) && u.active);
  }, [units]);
  const unitName = (id: string | null) => (units.kind === 'ready' ? (units.data.units.find((u) => u.id === id)?.name ?? '') : '');

  const path = useMemo(() => {
    const q = new URLSearchParams();
    if (unitId) q.set('unitId', unitId);
    if (status) q.set('status', status);
    if (category) q.set('category', category);
    if (from) q.set('from', new Date(from).toISOString());
    // The day picked as "to" is included: exclusive bound at the next midnight.
    if (to) q.set('to', new Date(new Date(to).getTime() + 86_400_000).toISOString());
    const s = q.toString();
    return `/exports/${dataset}${s ? `?${s}` : ''}`;
  }, [dataset, unitId, status, category, from, to]);

  const pick = (next: ExportDataset) => {
    setDataset(next);
    setStatus('');
    setResult({ kind: 'idle' });
  };

  const run = async () => {
    setResult({ kind: 'busy' });
    try {
      const table = await apiGet(path, exportResponseSchema);
      const filename = exportFileName(table, format);
      saveBlob(format === 'csv' ? toCsv(table, i18n.language) : toXlsx(table, i18n.language), filename);
      setResult({ kind: 'done', table, filename });
    } catch (error) {
      const apiError = error instanceof ApiClientError ? error : null;
      setResult({ kind: 'error', code: apiError?.code ?? 'internal', requestId: apiError?.requestId ?? '' });
    }
  };

  const statusOptions = STATUS_OPTIONS[dataset];
  const unitFilterApplies = dataset !== 'inventory';

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="exports-page">
      <h2 className="text-lg font-semibold text-slate-900">{t('exports.title')}</h2>
      <p className="mt-1 text-sm text-slate-500">{t('exports.hint')}</p>

      <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label={t('exports.dataset')}>
        {datasets.map((d) => (
          <button
            key={d}
            type="button"
            role="tab"
            aria-selected={dataset === d}
            onClick={() => pick(d)}
            className={`rounded-lg px-3 py-1.5 text-sm ring-1 transition ${dataset === d ? 'bg-slate-900 text-white ring-slate-900' : 'text-slate-700 ring-slate-300 hover:bg-slate-100'}`}
            data-testid={`exports-dataset-${d}`}
          >
            {t(`exports.datasets.${d}`)}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-500">{t(`exports.describe.${dataset}`, { max: EXPORT_MAX_ROWS })}</p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        {unitFilterApplies && (
          <label className="text-xs text-slate-600">
            {t('exports.unit')}
            <select className={input} value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="exports-unit">
              <option value="">{t('exports.anyUnit')}</option>
              {viewableUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.type === 'team' ? `${unitName(u.parentId)} / ${u.name}` : u.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="text-xs text-slate-600">
          {t(dataset === 'ledger' ? 'ledger.type' : 'exports.status')}
          <select className={input} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="exports-status">
            <option value="">{t('exports.any')}</option>
            {statusOptions.values.map((s) => (
              <option key={s} value={s}>{t(`${statusOptions.labelPrefix}.${s}`)}</option>
            ))}
          </select>
        </label>
        {dataset !== 'soldiers' && (
          <label className="text-xs text-slate-600">
            {t('exports.category')}
            <select className={input} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="exports-category">
              <option value="">{t('exports.any')}</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
        )}
        {dataset !== 'soldiers' && (
          <>
            <label className="text-xs text-slate-600">
              {t('ledger.from')}
              <input type="date" className={`${input} latin-inline`} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="exports-from" />
            </label>
            <label className="text-xs text-slate-600">
              {t('ledger.to')}
              <input type="date" className={`${input} latin-inline`} value={to} onChange={(e) => setTo(e.target.value)} data-testid="exports-to" />
            </label>
          </>
        )}
        <label className="text-xs text-slate-600">
          {t('exports.format')}
          <select className={input} value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)} data-testid="exports-format">
            <option value="xlsx">Excel (.xlsx)</option>
            <option value="csv">CSV</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => void run()}
          disabled={result.kind === 'busy'}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
          data-testid="exports-download"
        >
          {result.kind === 'busy' ? t('exports.preparing') : t('exports.download')}
        </button>
      </div>

      {result.kind === 'error' && <div className="mt-3"><ApiError code={result.code} requestId={result.requestId} /></div>}
      {result.kind === 'done' && (
        <p className="mt-3 text-sm text-slate-600" data-testid="exports-done">
          {t('exports.done', { count: result.table.rows.length, filename: result.filename })}
          {result.table.truncated && (
            <span className="ms-2 text-amber-700" data-testid="exports-truncated">{t('exports.truncated', { max: EXPORT_MAX_ROWS })}</span>
          )}
        </p>
      )}
    </section>
  );
}
